import { type Stripe, loadStripe } from '@stripe/stripe-js';

import { ENV } from '@/shared/config/env';
import { logger } from '@/shared/lib/logger';

import { apiClient } from './client';
import {
  PACKAGES_QUERY,
  PAYMENT_METHODS_QUERY,
  SUBSCRIPTIONS_QUERY,
} from './graphql/queries/payment';

// Stripe instance singleton
let stripePromise: Promise<Stripe | null> | null = null;

// ===== TYPE DEFINITIONS FROM BACKEND =====

// Backend payment method (what we store in DB)
export type BackendPaymentMethod = {
  id: number;
  stripeId: string;
  type: string;
  cardBrand?: string;
  cardExpMonth?: number;
  cardExpYear?: number;
  cardLast4?: string;
};

// Backend package for credits purchase
export type CreditPackage = {
  id: number;
  credits: number;
  amount: number; // in cents (799 = €7.99)
  currency: string; // 'eur' from backend
};

// Backend subscription plan
export type BackendSubscription = {
  id: number;
  name: string;
  description: string | null; // Detailed description of subscription benefits and features
  type: string;
  frequency: string; // 'weekly', 'monthly', 'yearly'
  amount: number; // in cents
  currency: string;
  credits: number; // Number of credits/tokens included
};

// User Payment record
export type UserPayment = {
  id: number;
  amount: number;
  currency: string;
  method: string;
  status: string;
  type: string;
  stripePaymentIntentId?: string;
  createdAt: string;
};

// User Subscription record from /auth/me
export type UserSubscription = {
  id: number;
  status: string;
  startAt: string;
  endAt: string;
  cancelledAt?: string; // Date when subscription was cancelled
  stripeSubscriptionId: string;
  subscription: {
    id: number;
    name: string;
    type: string;
    frequency: string;
    amount: number;
    currency: string;
  };
  cancelAtPeriodEnd?: boolean; // This field might not be in the response yet
};

// Promotion Code Validation response
export type PromotionCodeValidation = {
  valid: boolean;
  code: string;
  type: 'percentage' | 'fixed';
  percentOff?: number; // Present when type: percentage (0-100)
  amountOff?: number; // Present when type: fixed (in cents)
  currency?: string; // Present when type: fixed (e.g., 'usd', 'eur')
  duration: 'once' | 'repeating' | 'forever';
  durationInMonths: number;
};

// ===== RESPONSE TYPES =====

export type PaymentMethodResponse = {
  paymentMethod: BackendPaymentMethod;
};

export type PurchaseCreditsResponse = {
  userPayment?: UserPayment;
  credits?: number;
  clientSecret?: string; // For 3D Secure confirmation
};

export type SubscriptionResponse = {
  userSubscription?: UserSubscription;
  credits?: number;
  clientSecret?: string; // For 3D Secure confirmation
};

// ===== STRIPE SERVICE CLASS =====
class StripeService {
  // Get Stripe instance
  async getStripe(): Promise<Stripe | null> {
    if (!stripePromise) {
      stripePromise = loadStripe(ENV.STRIPE_PUBLISHABLE_KEY);
    }
    return stripePromise;
  }

  // ========== PAYMENT METHODS ==========

  /**
   * Create and attach a new payment method to user (deprecated - use Elements directly)
   * This method is deprecated. Use the StripeCardElement component instead.
   */

  /**
   * Get all saved payment methods for current user
   */
  async getPaymentMethods(): Promise<BackendPaymentMethod[]> {
    const response = await apiClient.post('/graphql', {
      query: PAYMENT_METHODS_QUERY,
    });

    return response.data?.data?.paymentMethods || [];
  }

  // ========== CREDITS PURCHASE - WEB FLOW ==========

  /**
   * Purchase credits - Web flow
   *
   * Flow:
   * 1. User selects a package
   * 2. User selects/adds payment method
   * 3. Call this method to create PaymentIntent
   * 4. If clientSecret returned, confirm with 3D Secure
   * 5. Backend webhook confirms final payment
   *
   * @param selectedPackage - Package to purchase
   * @param paymentMethodId - Stripe payment method ID (optional, uses default)
   */
  async purchaseCredits(
    selectedPackage: CreditPackage,
    paymentMethodId?: string,
  ): Promise<PurchaseCreditsResponse> {
    // Backend creates PaymentIntent and charges immediately if possible
    const response = await apiClient.post('/credits/purchase', {
      credits: selectedPackage.credits,
      currency: selectedPackage.currency,
      stripePaymentMethodId: paymentMethodId,
    });

    // Response includes:
    // - userPayment: payment record in DB
    // - credits: updated credit balance
    // - clientSecret: only if 3D Secure is required
    return {
      userPayment: response.data.userPayment,
      credits: response.data.credits,
      clientSecret: response.data.clientSecret,
    };
  }

  /**
   * Confirm payment with 3D Secure
   * Call this if purchaseCredits returns a clientSecret
   *
   * @param clientSecret - From purchaseCredits response
   * @param paymentMethodId - Payment method to use for confirmation
   */
  async confirmPaymentWith3DSecure(clientSecret: string, paymentMethodId: string): Promise<void> {
    const stripe = await this.getStripe();
    if (!stripe) throw new Error('Stripe not initialized');

    const result = await stripe.confirmCardPayment(clientSecret, {
      payment_method: paymentMethodId,
      setup_future_usage: 'off_session', // Save card for future use
    });

    if (result.error) {
      throw new Error(result.error.message || 'Payment failed');
    }

    // Payment confirmed! Backend will receive webhook and add credits
  }

  // ========== SUBSCRIPTIONS ==========

  /**
   * Subscribe to a plan
   * Similar flow to credits purchase - may require 3D Secure
   */
  async subscribe(
    subscriptionId: number,
    paymentMethodId?: string,
    promotionCode?: string,
  ): Promise<SubscriptionResponse> {
    const response = await apiClient.post('/subscription/subscribe', {
      subscriptionId,
      stripePaymentMethodId: paymentMethodId,
      promotionCode,
    });

    return {
      userSubscription: response.data.data.userSubscription,
      credits: response.data.data.credits,
      clientSecret: response.data.data.clientSecret, // For 3D Secure if needed
    };
  }

  /**
   * Validate a promotion code
   * @param code - Promotion code to validate
   * @returns Promotion code validation details
   */
  async validatePromotionCode(code: string): Promise<PromotionCodeValidation> {
    const response = await apiClient.post('/subscription/validate-promotion-code', {
      code,
    });

    return response.data.data;
  }

  /**
   * Cancel subscription
   */
  async unsubscribe(): Promise<void> {
    await apiClient.post('/subscription/unsubscribe', {});
  }

  /**
   * Reactivate cancelled subscription
   */
  async reactivateSubscription(): Promise<void> {
    await apiClient.post('/subscription/reactivate', {});
  }

  // ========== DATA FETCHING ==========

  /**
   * Get available credit packages
   */
  async getCreditPackages(): Promise<CreditPackage[]> {
    const response = await apiClient.post('/graphql', {
      query: PACKAGES_QUERY,
    });

    return response.data?.data?.packages || [];
  }

  /**
   * Get available subscription plans
   */
  async getSubscriptionPlans(): Promise<BackendSubscription[]> {
    const response = await apiClient.post('/graphql', {
      query: SUBSCRIPTIONS_QUERY,
    });

    return response.data?.data?.subscriptions || [];
  }

  /**
   * Get current user's subscription from cached user data
   * This avoids making additional /auth/me calls
   */
  async getUserSubscription(): Promise<UserSubscription | null> {
    try {
      // Fallback to API call since we can't use hooks in class methods
      logger.stripe.debug('Making API call for subscription data');
      const response = await apiClient.get('/auth/me');
      // Extract subscription info from user data if it exists
      // The API returns data.data.userSubscription structure
      const userData = response.data?.data || response.data;
      return userData?.userSubscription || null;
    } catch (error: unknown) {
      logger.stripe.error('Failed to fetch user subscription', { error });
      return null;
    }
  }

  // ========== HELPER METHODS ==========

  /**
   * Format price for display (EUR)
   */
  formatPrice(amount: number): string {
    const price = amount / 100;
    // Remove .00 for whole numbers, keep decimals for non-whole numbers
    return price % 1 === 0 ? `€${price.toFixed(0)}` : `€${price.toFixed(2)}`;
  }

  /**
   * Get error message from Stripe error
   */
  getErrorMessage(error: unknown): string {
    if (error && typeof error === 'object' && 'message' in error) {
      return (error as { message: string }).message;
    }
    if (typeof error === 'string') {
      return error;
    }
    return 'An error occurred. Please try again.';
  }
}

// Export singleton instance
export const stripeService = new StripeService();
